import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../../../../../components/Table';
import Modal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { GoArrowUpRight } from 'react-icons/go';
import { Tooltip } from 'react-tooltip';
import {
  CloseStyle,
  Container,
  TableContainerStyle,
  Title,
  DescriptionStyle,
  ConfidenceButtonStyle,
  ProgressCellStyle,
  ProgressTrackStyle,
  ProgressFillStyle,
  ProgressTextStyle,
  DetailsModalBodyStyle,
  DetailsHeaderStyle,
  ScoreRingStyle,
  StatusPillStyle,
  DetailGridStyle,
  DetailCardStyle,
  EvidenceListStyle,
  EvidenceItemStyle,
  SectionHeaderStyle,
  VerdictSummaryStyle,
  VerdictPillStyle,
  EvidenceHeaderStyle,
  EvidenceBadgeGroupStyle,
  EvidenceCategoryBadgeStyle,
  FieldConfidenceBadgeStyle,
  EvidenceValueStyle,
  EvidenceReasonStyle,
  DetailsModalStyles,
  customStyles
} from './styles';
import agentStore from '../../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../../components/ChatWidget/constants';
import { TbSearch } from 'react-icons/tb';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import { Flex, Text, TextInput } from '@mantine/core';
import { SortButton } from '../../../components/SortButton';
import { FilterDropdown } from '../../../components/ProjectFilters/FilterDropdown';
import { useGetWorkflowExtract } from '../../../../../services/workflow.services';
import { idCreator } from '../../../utils/idCreator';
import { useSignalRListener } from '../../../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../../../config/signalr';

const INITIAL_PROGRESS_START_PERCENTAGE = 10;
const INITIAL_PROGRESS_MAX_PERCENTAGE = 95;
const INITIAL_PROGRESS_STORAGE_KEY_PREFIX = 'initial-extraction-progress-start';

function flattenData(data) {
  const result = [];

  data.forEach(file => {
    const { id: file_id, name: file_name, sheets } = file;

    sheets.forEach(sheet => {
      const { name: sheet_name, id: sheet_id, extractions } = sheet;

      extractions.forEach(extraction => {
        const { category, id: extraction_id } = extraction;

        result.push({
          ...file,
          ...sheet,
          ...extraction,
          file_id,
          file_name,
          sheet_name,
          sheet_id,
          category,
          extraction_id
        });
      });
    });
  });

  return result;
}

export const InitialSetupExtractionModal = ({ isOpen, onClose, periodId, worklowId }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedConfidenceRecord, setSelectedConfidenceRecord] = useState(null);
  const [selectedEvidenceVerdict, setSelectedEvidenceVerdict] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [progressNow, setProgressNow] = useState(Date.now());
  const progressStartTimeByTaskRef = useRef({});

  const verdictTones = {
    matched: { color: '#2F9E44', background: '#EBFBEE', border: '#B2F2BB' },
    uncertain: { color: '#F08C00', background: '#FFF4E6', border: '#FFD8A8' },
    mismatch: { color: '#E03131', background: '#FFF5F5', border: '#FFC9C9' },
    missing_source: { color: '#5C5F66', background: '#F1F3F5', border: '#DEE2E6' },
    verified: { color: '#2F9E44', background: '#EBFBEE', border: '#B2F2BB' },
    needs_review: { color: '#F08C00', background: '#FFF4E6', border: '#FFD8A8' },
    unverified: { color: '#5C5F66', background: '#F1F3F5', border: '#DEE2E6' }
  };

  const getConfidenceScore = record => {
    const confidenceScore = record.confidence_score;
    if (typeof confidenceScore !== 'number') return null;
    return Math.min(100, Math.max(0, confidenceScore));
  };

  const getScoreColor = score => {
    if (score >= 85) return '#2F9E44';
    if (score >= 70) return '#F08C00';
    return '#E03131';
  };

  const getFieldConfidenceScore = item => {
    if (typeof item.confidence_score === 'number')
      return Math.min(100, Math.max(0, item.confidence_score));
    const verdictScores = { matched: 98, uncertain: 72, missing_source: 55, mismatch: 25 };
    return verdictScores[item.verdict] ?? 55;
  };

  const getEvidenceItemId = item =>
    idCreator(
      [item.extraction_category, item.field, item.source_reference, item.verdict]
        .filter(Boolean)
        .join('-')
    );

  const apiParams = useMemo(() => {
    const params = { workflow_id: worklowId, period_id: periodId };
    if (debouncedSearch) params.search_query = debouncedSearch;
    if (selectedCategory) params.category_name = selectedCategory.name;
    return params;
  }, [worklowId, periodId, debouncedSearch, selectedCategory]);

  const {
    data: extractions,
    isLoading,
    refetch
  } = useGetWorkflowExtract({
    params: apiParams,
    queryParams: {
      enabled: !!worklowId && !!periodId && !!isOpen
    }
  });

  const mutatedExtrations = useMemo(() => {
    if (!extractions || extractions.length === 0) return [];

    return flattenData(extractions);
  }, [extractions]);

  useEffect(() => {
    if (!mutatedExtrations.length) {
      setCategoryOptions([]);
      return;
    }
    if (!selectedCategory) {
      const unique = [...new Set(mutatedExtrations.map(item => item.category).filter(Boolean))];
      setCategoryOptions(unique.sort().map(category => ({ id: category, name: category })));
    }
  }, [mutatedExtrations, selectedCategory]);

  const visibleData = useMemo(() => {
    if (!sortConfig.key) return mutatedExtrations;
    const isAscending = !sortConfig.direction.startsWith('-');
    const sortField = sortConfig.key;
    return [...mutatedExtrations].sort((a, b) => {
      const valueA = (a[sortField] ?? '').toLowerCase();
      const valueB = (b[sortField] ?? '').toLowerCase();
      return isAscending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  }, [mutatedExtrations, sortConfig]);

  useSignalRListener(SIGNALR_LISTENERS.ON_EXTRACTION_STATUS_UPDATED, () => {
    if (isOpen) refetch();
  });

  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED, () => {
    if (isOpen) refetch();
  });

  useEffect(() => {
    const inProgressTaskIds = new Set();
    const now = Date.now();

    (visibleData ?? []).forEach(record => {
      const task = record.latest_task;
      if (String(task?.status || '').toLowerCase() !== 'in progress' || task.id == null) return;

      const taskId = String(task.id);
      inProgressTaskIds.add(taskId);
      if (!progressStartTimeByTaskRef.current[taskId]) {
        const storageKey = `${INITIAL_PROGRESS_STORAGE_KEY_PREFIX}-${taskId}`;
        const storedTimestamp = Number(window.sessionStorage.getItem(storageKey));
        const startTimestamp = storedTimestamp || now;
        progressStartTimeByTaskRef.current[taskId] = startTimestamp;
        window.sessionStorage.setItem(storageKey, String(startTimestamp));
      }
    });

    Object.keys(progressStartTimeByTaskRef.current).forEach(taskId => {
      if (!inProgressTaskIds.has(taskId)) {
        delete progressStartTimeByTaskRef.current[taskId];
        window.sessionStorage.removeItem(`${INITIAL_PROGRESS_STORAGE_KEY_PREFIX}-${taskId}`);
      }
    });

    if (!isOpen || inProgressTaskIds.size === 0) return undefined;

    setProgressNow(now);
    const intervalId = window.setInterval(() => setProgressNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isOpen, visibleData]);

  const getEstimatedTaskDuration = record => {
    let duration = 2 * 60 * 1000;
    if (!record.is_structured) duration *= 1.8;

    const category = String(record.category || '').toLowerCase();
    if (category.includes('lookup')) duration *= 0.65;
    if (category.includes('shareholder')) duration *= 0.85;
    if (category.includes('turnover')) duration *= 1.15;
    if (category.includes('exposure')) duration *= 1.35;

    const seed = String(record.extraction_id || record.sheet_id || record.latest_task?.id || '')
      .split('')
      .reduce((sum, character) => sum + character.charCodeAt(0), 0);
    return duration * (0.85 + (seed % 40) / 100);
  };

  const getProgressPercentage = record => {
    const task = record.latest_task;
    const status = String(task?.status || '').toLowerCase();
    if (status === 'completed') return 100;
    if (['failed', 'cancelled', 'not started'].includes(status)) return 0;
    if (status !== 'in progress') return null;

    const backendPercentage =
      typeof task?.progress_percentage === 'number'
        ? Math.min(100, Math.max(0, Math.round(task.progress_percentage)))
        : INITIAL_PROGRESS_START_PERCENTAGE;
    const taskId = task?.id == null ? null : String(task.id);
    const startTime = taskId ? progressStartTimeByTaskRef.current[taskId] : null;
    if (!startTime) return backendPercentage;

    const elapsedRatio = Math.min((progressNow - startTime) / getEstimatedTaskDuration(record), 1);
    const estimatedPercentage = Math.round(
      INITIAL_PROGRESS_START_PERCENTAGE +
        elapsedRatio * (INITIAL_PROGRESS_MAX_PERCENTAGE - INITIAL_PROGRESS_START_PERCENTAGE)
    );
    return Math.min(
      INITIAL_PROGRESS_MAX_PERCENTAGE,
      Math.max(backendPercentage, estimatedPercentage)
    );
  };

  const onNavigate = (fileId, sheetId, category, extractId) => {
    navigate(
      `/data-extraction/${worklowId}/${periodId}?fileId=${fileId}&sheetId=${sheetId}&category=${category}&extractId=${extractId}`
    );
  };

  const columns = [
    {
      key: 'file_name',
      title: 'File name',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            File name
          </Text>
          <SortButton
            columnName="file_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </Flex>
      )
    },
    {
      key: 'sheet_name',
      title: 'Sheet name',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            Sheet name
          </Text>
          <SortButton
            columnName="sheet_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </Flex>
      )
    },
    {
      key: 'category',
      title: 'Category',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            Category
          </Text>
          <SortButton columnName="category" setSortConfig={setSortConfig} sortConfig={sortConfig} />
        </Flex>
      ),
      render: record => {
        return (
          <DescriptionStyle>
            <span>{record.category}</span>
            <GoArrowUpRight
              onClick={() =>
                onNavigate(record.file_id, record.sheet_id, record.category, record.extraction_id)
              }
              color="#228BE6"
              style={{
                cursor: 'pointer'
              }}
              data-testid="go-arrow-up-right"
            />
          </DescriptionStyle>
        );
      }
    },
    {
      key: 'progress_percentage',
      title: 'Progress',
      renderHeadCell: () => (
        <Text fz="14px" lh="20px" c="#495057" fw="600">
          Progress
        </Text>
      ),
      render: record => {
        const percentage = getProgressPercentage(record);
        if (percentage == null) return '-';

        return (
          <ProgressCellStyle id={`initial-extraction-progress-${record.extraction_id}`}>
            <ProgressTrackStyle id={`initial-extraction-progress-track-${record.extraction_id}`}>
              <ProgressFillStyle
                id={`initial-extraction-progress-fill-${record.extraction_id}`}
                $percentage={percentage}
              />
            </ProgressTrackStyle>
            <ProgressTextStyle id={`initial-extraction-progress-text-${record.extraction_id}`}>
              {percentage}%
            </ProgressTextStyle>
          </ProgressCellStyle>
        );
      }
    },
    {
      key: 'confidence_score',
      title: 'Confidence Score',
      renderHeadCell: () => (
        <Text fz="14px" lh="20px" c="#495057" fw="600">
          Confidence Score
        </Text>
      ),
      render: record => {
        const confidenceScore = getConfidenceScore(record);
        const isVerifierCalculating =
          confidenceScore == null &&
          Boolean(record.extraction_id) &&
          record.latest_task?.status === 'completed';
        return (
          <ConfidenceButtonStyle
            id={`initial-extraction-confidence-${record.extraction_id}`}
            type="button"
            $score={confidenceScore}
            disabled={confidenceScore == null || !record.ai_extraction_details}
            onClick={() => {
              setSelectedEvidenceVerdict(null);
              setSelectedConfidenceRecord(record);
            }}
          >
            {confidenceScore == null
              ? isVerifierCalculating
                ? 'Calculating'
                : '-'
              : `${confidenceScore.toFixed(1)}%`}
          </ConfidenceButtonStyle>
        );
      }
    }
  ];

  useDidUpdate(() => {
    if (isOpen) {
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.AutomationExtractionDashboard);
    } else {
      agentStore.setCurrentSubScreen('');
      setSortConfig({ key: '', direction: '' });
      setSearchTerm('');
      setSelectedCategory(null);
      setSelectedConfidenceRecord(null);
      setSelectedEvidenceVerdict(null);
    }
  }, [isOpen]);

  const selectedDetails = selectedConfidenceRecord?.ai_extraction_details ?? {};
  const selectedEvidence = Array.isArray(selectedDetails.evidence) ? selectedDetails.evidence : [];
  const selectedScore = selectedConfidenceRecord
    ? getConfidenceScore(selectedConfidenceRecord)
    : null;
  const selectedStatus = selectedDetails.status || 'unverified';
  const selectedScoreColor = getScoreColor(selectedScore ?? 0);
  const filteredEvidence = selectedEvidenceVerdict
    ? selectedEvidence.filter(
        item => (item.verdict || 'missing_source') === selectedEvidenceVerdict
      )
    : selectedEvidence;
  const verdictCounts = selectedEvidence.reduce((acc, item) => {
    const verdict = item.verdict || 'missing_source';
    acc[verdict] = (acc[verdict] || 0) + 1;
    return acc;
  }, {});

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
      <Container>
        <CloseStyle as="button" type="button" aria-label="close" onClick={onClose}>
          <IoClose fontSize="18px" color="#868E96" />
        </CloseStyle>
        <Title>Extractions List</Title>
        <Flex gap={16} align="center" mb={16}>
          <TextInput
            placeholder="Search"
            leftSection={<TbSearch size={16} color="#ADB5BD" />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="sm"
            w={256}
          />
          <FilterDropdown
            label="Category"
            placeholder="All Categories"
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
          />
        </Flex>
        <TableContainerStyle>
          <Table columns={columns} hasIndex={false} data={visibleData} isLoading={isLoading} />
        </TableContainerStyle>
        <Tooltip
          id="warning-tooltip"
          style={{
            background: '#868E96',
            padding: '8px'
          }}
        />
      </Container>
      <Modal
        isOpen={!!selectedConfidenceRecord}
        onRequestClose={() => {
          setSelectedEvidenceVerdict(null);
          setSelectedConfidenceRecord(null);
        }}
        style={DetailsModalStyles}
      >
        <DetailsModalBodyStyle id="initial-ai-extraction-details-modal">
          <CloseStyle
            id="initial-ai-extraction-details-close"
            as="button"
            type="button"
            aria-label="close AI extraction details"
            onClick={() => {
              setSelectedEvidenceVerdict(null);
              setSelectedConfidenceRecord(null);
            }}
          >
            <IoClose fontSize="18px" color="#868E96" />
          </CloseStyle>
          <Title id="initial-ai-extraction-details-title">AI Extraction Details</Title>
          <DetailsHeaderStyle id="initial-ai-extraction-details-header">
            <ScoreRingStyle
              id="initial-ai-extraction-confidence-ring"
              $score={selectedScore ?? 0}
              $color={selectedScoreColor}
            >
              <Text
                id="initial-ai-extraction-confidence-value"
                fz="24px"
                lh="28px"
                fw={800}
                c={selectedScoreColor}
              >
                {selectedScore == null ? '-' : `${selectedScore.toFixed(1)}%`}
              </Text>
              <Text
                id="initial-ai-extraction-confidence-label"
                fz="11px"
                lh="14px"
                c="#868E96"
                fw={700}
                tt="uppercase"
              >
                Confidence
              </Text>
            </ScoreRingStyle>
            <div id="initial-ai-extraction-details-overview">
              <StatusPillStyle
                id="initial-ai-extraction-details-status"
                $tone={verdictTones[selectedStatus]}
              >
                {selectedStatus.replace('_', ' ')}
              </StatusPillStyle>
              <Text
                id="initial-ai-extraction-details-summary"
                fz="14px"
                lh="22px"
                c="#25262B"
                fw={500}
                mt={12}
              >
                {selectedDetails.summary ||
                  'The extracted values were checked against the source sheet.'}
              </Text>
              <Text
                id="initial-ai-extraction-details-source"
                fz="12px"
                lh="18px"
                c="#868E96"
                mt={10}
              >
                {selectedConfidenceRecord?.file_name || '-'} ·{' '}
                {selectedConfidenceRecord?.sheet_name || '-'} ·{' '}
                {selectedConfidenceRecord?.category || '-'}
              </Text>
            </div>
          </DetailsHeaderStyle>
          <DetailGridStyle id="initial-ai-extraction-details-metrics">
            <DetailCardStyle id="initial-ai-extraction-details-matched">
              <Text fz="11px" c="#868E96" tt="uppercase" fw={700}>
                Matched Fields
              </Text>
              <Text fz="18px" c="#25262B" fw={700}>
                {selectedDetails.matched_fields ?? '-'} / {selectedDetails.checked_fields ?? '-'}
              </Text>
            </DetailCardStyle>
            <DetailCardStyle id="initial-ai-extraction-details-review">
              <Text fz="11px" c="#868E96" tt="uppercase" fw={700}>
                Needs Review
              </Text>
              <Text fz="18px" c="#25262B" fw={700}>
                {selectedDetails.needs_review_fields ?? '-'}
              </Text>
            </DetailCardStyle>
            <DetailCardStyle id="initial-ai-extraction-details-evidence-count">
              <Text fz="11px" c="#868E96" tt="uppercase" fw={700}>
                Source Evidence
              </Text>
              <Text fz="18px" c="#25262B" fw={700}>
                {selectedEvidence.length}
              </Text>
            </DetailCardStyle>
          </DetailGridStyle>
          <SectionHeaderStyle id="initial-ai-extraction-evidence-header">
            <Text id="initial-ai-extraction-evidence-title" fz="15px" c="#25262B" fw={700}>
              Source Evidence
            </Text>
            <VerdictSummaryStyle id="initial-ai-extraction-verdict-summary">
              {Object.entries(verdictCounts).map(([verdict, count]) => (
                <VerdictPillStyle
                  id={`initial-ai-extraction-verdict-${verdict}`}
                  key={verdict}
                  $tone={verdictTones[verdict]}
                  $active={selectedEvidenceVerdict === verdict}
                  type="button"
                  onClick={() =>
                    setSelectedEvidenceVerdict(current => (current === verdict ? null : verdict))
                  }
                >
                  {verdict.replace('_', ' ')}: {count}
                </VerdictPillStyle>
              ))}
            </VerdictSummaryStyle>
          </SectionHeaderStyle>
          <EvidenceListStyle id="initial-ai-extraction-evidence-list">
            {filteredEvidence.length > 0 ? (
              filteredEvidence.map(item => {
                const evidenceItemId = getEvidenceItemId(item);

                return (
                  <EvidenceItemStyle
                    id={`initial-ai-extraction-evidence-${evidenceItemId}`}
                    key={evidenceItemId}
                    $tone={verdictTones[item.verdict]}
                  >
                    <EvidenceHeaderStyle
                      id={`initial-ai-extraction-evidence-header-${evidenceItemId}`}
                    >
                      <Text fz="14px" fw={800} c="#25262B">
                        {item.field}
                      </Text>
                      <EvidenceBadgeGroupStyle
                        id={`initial-ai-extraction-evidence-badges-${evidenceItemId}`}
                      >
                        <EvidenceCategoryBadgeStyle>
                          {item.extraction_category || selectedConfidenceRecord?.category || '-'}
                        </EvidenceCategoryBadgeStyle>
                        <VerdictPillStyle $tone={verdictTones[item.verdict]} type="button" disabled>
                          {(item.verdict || '-').replace('_', ' ')}
                        </VerdictPillStyle>
                        <FieldConfidenceBadgeStyle $score={getFieldConfidenceScore(item)}>
                          {getFieldConfidenceScore(item).toFixed(1)}% field confidence
                        </FieldConfidenceBadgeStyle>
                      </EvidenceBadgeGroupStyle>
                    </EvidenceHeaderStyle>
                    <div id={`initial-ai-extraction-evidence-values-${evidenceItemId}`}>
                      <EvidenceValueStyle>
                        <Text fz="11px" c="#868E96" fw={700}>
                          Extracted
                        </Text>
                        <Text fz="12px" c="#25262B" fw={700}>
                          {item.extracted_value || '-'}
                        </Text>
                      </EvidenceValueStyle>
                      <EvidenceValueStyle>
                        <Text fz="11px" c="#868E96" fw={700}>
                          Source
                        </Text>
                        <Text fz="12px" c="#25262B">
                          {item.source_reference || '-'}
                        </Text>
                      </EvidenceValueStyle>
                    </div>
                    <EvidenceReasonStyle
                      id={`initial-ai-extraction-evidence-reason-${evidenceItemId}`}
                    >
                      {item.reason || '-'}
                    </EvidenceReasonStyle>
                  </EvidenceItemStyle>
                );
              })
            ) : (
              <Text id="initial-ai-extraction-evidence-empty" fz="12px" c="#868E96">
                No field-level source evidence was returned by the verifier.
              </Text>
            )}
          </EvidenceListStyle>
        </DetailsModalBodyStyle>
      </Modal>
    </Modal>
  );
};
